import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Switch,
  TextField,
  Typography,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SendIcon from "@mui/icons-material/Send";
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  onSnapshot,
  query,
  orderBy,
  Timestamp,
  getDocs,
  where,
  writeBatch,
} from "firebase/firestore";
import { DATABASE } from "../../../Config/firebase";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { useFormik } from "formik";
import * as Yup from "yup";
import dayjs from "dayjs";
import { dolorFormatAmount } from "../../../Utils/Services";
import FUNCTIONS_URLS from '../../../Config/functions';
import { adminAuthHeaders } from '../../../Config/adminAuthHeaders';
import FundsPageShell from "../../../Components/FundsPeriodTotals/FundsPageShell";
import FundsPageToolbar from "../../../Components/FundsPeriodTotals/FundsPageToolbar";
import useDebounce from "../../../Components/useDebounce/useDebounce";
import useYieldProductsPeriodTotals from "../../../hooks/useYieldProductsPeriodTotals";
import {
  filterYieldProductsByPeriod,
  getDefaultFundsPeriodEnd,
  getDefaultFundsPeriodStart,
  hasActiveFundsFilters,
  matchesYieldProductStatus,
  productMatchesClientSegment,
  YIELD_PRODUCT_STATUS_OPTIONS,
} from "../../../Utils/fundsPeriodTotals";


// Default templates for Yield Product terms - SIMPLE VERSION (APP)
const DEFAULT_TERMS_TOGGLE_ON_SIMPLE = `FIDDUX YIELD PRODUCT PARTICIPATION AGREEMENT

Welcome to the FIDDUX Yield Product. By activating this feature, you are entering into a participation agreement that will allow your account balance to earn additional yield returns through our managed investment strategies.

PARTICIPATION COMMENCEMENT
Your participation in this Yield Product will begin the day after you activate this feature. This means that if you activate today, your balance will start earning yield starting tomorrow. The calculation is based on your net account balance at the fund's daily closing time, which ensures accurate and fair participation tracking.

YIELD CALCULATION METHOD
The yield you earn is calculated on a daily basis, proportional to the number of days you participate in each month. This means that if you participate for 15 days in a month, you will earn yield for those 15 days only. The calculation is not compounded - each day's yield is calculated independently based on that day's balance and the applicable monthly rate.

Monthly yield rates are determined by the fund administrator in collaboration with our qualified third-party investment partners. These rates may vary from month to month depending on market conditions, partner performance, and strategic decisions. The rates are set to provide competitive returns while maintaining appropriate risk management protocols.

PAYMENT TERMS
Unlike some investment products that pay monthly, this Yield Product accumulates your earnings throughout the entire product period. You will receive a single payment at the end of the product period, after all calculations have been verified by the administrator and our partners. This approach allows for more efficient fund management and ensures accurate final calculations.

During the product period, you will not see monthly payments or previews of your accumulated yield in your account. This is by design - all earnings are calculated and held until the final payment date. You can rest assured that your yield is being tracked accurately throughout the period.

DEACTIVATION AND REACTIVATION
You have the flexibility to deactivate your participation at any time. When you deactivate, the change takes effect the following day, meaning you will continue earning yield for the remainder of the current day. If you choose to reactivate later, you will need to accept the terms that are in effect at that time, which may include different rates or conditions for new product periods.

Please note that frequent toggling of your participation (more than 3 times per month) may result in restricted access at the administrator's discretion. This policy helps maintain system stability and ensures fair participation for all clients.

PRODUCT TRANSITION AND FUTURE PRODUCTS
This Yield Product operates on a defined period basis. At the end of each product period, if you remain active, you may be automatically enrolled in the next product period with updated terms and conditions. If you have deactivated, you will need to review and accept new terms to participate in future products.

Future products may involve different third-party partners, updated rates, or modified terms. It is your responsibility to review any new terms before reactivating participation.

ADMINISTRATOR DISCRETION AND FINAL DECISIONS
All yield calculations, rate determinations, and payment decisions are made by the fund administrator in consultation with our investment partners. These decisions are final and binding. The administrator reserves the right to adjust calculations, modify terms, or make other determinations as necessary to ensure proper fund management and compliance with applicable regulations.

RISK ACKNOWLEDGMENT
While we work with qualified third-party partners and employ professional investment strategies, yield returns are not guaranteed. Market conditions, partner performance, and other factors may affect the actual returns you receive. Past performance does not indicate future results. You should only participate with funds that you can afford to have committed to this product for the duration of the product period.

GOVERNING LAW
This participation agreement is governed by the laws of the Republic of Estonia. Any disputes or legal matters arising from this agreement shall be subject to the exclusive jurisdiction of the courts of Tallinn, Estonia.

LIMITATION OF LIABILITY
FIDDUX and its third-party partners shall not be liable for any losses, damages, or claims arising from your participation in this Yield Product, except as may be required by applicable Estonian law. By participating, you acknowledge that you understand and accept these limitations.

ACKNOWLEDGMENT AND ACCEPTANCE
By checking the box below and activating this Yield Product, you confirm that you have read, understood, and agree to all the terms and conditions outlined above. You acknowledge that you have had the opportunity to review these terms and that you are entering into this agreement voluntarily and with full understanding of the participation requirements, payment terms, and risk factors involved.

If you have any questions about these terms or how the Yield Product works, please contact our support team before activating. Once activated, these terms will govern your participation until you deactivate or until the product period ends.

For additional legal information, please refer to our complete terms documentation available online.`;

// Default templates for Yield Product terms - FULL VERSION (WEB)
const DEFAULT_TERMS_TOGGLE_ON_FULL = `FIDDUX YIELD PRODUCT PARTICIPATION TERMS

Governing Law and Jurisdiction
This Yield Product Participation Agreement ("Agreement") is governed by the laws of the Republic of Estonia. Any disputes shall be subject to the exclusive jurisdiction of the courts of Tallinn, Estonia.

Participation Terms
By activating this Yield Product, you acknowledge and agree that:

Participation Commencement: Your account balance will begin participating in this Yield Product, managed in partnership with qualified third-party providers, starting the day after activation. Participation is calculated daily based on your net balance at the fund's closing time.

Yield Calculation and Rates:
Yield is calculated separately from your regular fund performance through specialized third-party strategies.
Monthly yield rates are determined by the fund administrator in collaboration with our partners and may vary each month. Returns are approximate and depend on market conditions.
Yield is calculated proportionally based on the number of days you participate each month. Yield is not compounded and is not cumulative.
Returns may vary and are not guaranteed. Past performance does not indicate future results.

Administrator Discretion:
All yield calculations, rate determinations, and payment decisions are final and binding. The administrator reserves the right to adjust calculations or terms in consultation with our partners.

Payment Terms:
Yield earnings are accumulated throughout the product period. Payment is made in a single transaction at the end of the product period, after administrator and partner verification.
You will not receive monthly payments or previews of accumulated yield during the product period.

Deactivation and Reactivation:
You may deactivate participation at any time, with effect from the following day. Deactivating and reactivating on the same day will result in zero yield for that day.
Frequent toggling (more than 3 times per month) may result in restricted access at the administrator's discretion.

Product Transition:
Future products may involve different third-party partners and require acceptance of then-current terms.

Limitation of Liability:
FIDDUX and its third-party partners shall not be liable for any losses, damages, or claims arising from yield participation, except as required by Estonian law.

Acknowledgment:
You confirm that you have read, understood, and agree to all terms and conditions herein.

By checking the box below, you confirm your acceptance of these terms.`;

// Default templates for Yield Product deactivation - SIMPLE VERSION (APP)
const DEFAULT_TERMS_TOGGLE_OFF_SIMPLE = `FIDDUX YIELD PRODUCT DEACTIVATION CONFIRMATION

You are about to deactivate your participation in the FIDDUX Yield Product. Please read this confirmation carefully to understand the implications of this action.

EFFECTIVE DATE OF DEACTIVATION
When you confirm this deactivation, your account balance will stop participating in the Yield Product starting the day after your deactivation. This means that if you deactivate today, you will continue earning yield for the remainder of today, but your balance will no longer participate starting tomorrow.

YIELD EARNED UP TO DEACTIVATION
Any yield that you have earned up to the point of deactivation will be calculated and included in your final payment at the end of the current product period. The calculation will be based on the number of days you participated and the applicable rates during your participation period. You will receive this payment along with all other participants at the scheduled end date of the product period.

IMPORTANT CONSIDERATIONS
Deactivating your participation may affect your eligibility for yield accumulation for the remainder of the current month. If you deactivate during an active month, you may not be eligible to reactivate and earn yield for the remainder of that month, depending on the specific terms of the current product period. This is an important consideration if you are thinking about temporarily deactivating and then reactivating.

Additionally, if you deactivate and then wish to participate in a future product period, you will need to review and accept the new terms and conditions that will be in effect at that time. These terms may include different yield rates, updated partner information, modified payment schedules, or other changes that reflect the evolution of our Yield Product offerings.

PRODUCT PERIOD COMPLETION
If you deactivate before the end of the current product period, you will still receive payment for the yield you earned during your participation, but you will not earn any additional yield for the remainder of the product period. This payment will be processed at the same time as payments for all other participants, at the end of the product period.

REACTIVATION REQUIREMENTS
If you choose to reactivate your participation in the future, you will be required to accept the terms and conditions that are in effect at that time. These may be different from the terms you accepted when you first activated, as our Yield Product offerings evolve and improve over time. You should review any new terms carefully before reactivating to ensure you understand all current requirements, rates, and conditions.

ADMINISTRATOR DISCRETION
The fund administrator reserves the right to make final decisions regarding yield calculations, payment processing, and eligibility for future product participation. All decisions made by the administrator are final and binding.

GOVERNING LAW
This deactivation confirmation is governed by the laws of the Republic of Estonia. Any disputes or legal matters arising from this deactivation or your previous participation shall be subject to the exclusive jurisdiction of the courts of Tallinn, Estonia.

ACKNOWLEDGMENT
By checking the box below and confirming this deactivation, you acknowledge that you have read, understood, and agree to all the terms and conditions outlined above. You confirm that you understand the implications of deactivating your participation, including the effect on your yield earnings, payment timing, and future participation requirements.

You also acknowledge that you are making this decision voluntarily and that you have had the opportunity to review these terms and consider your options before proceeding with the deactivation.

If you have any questions or concerns about deactivating your participation, please contact our support team before confirming this action. Once confirmed, the deactivation will take effect as described above.

For additional legal information regarding deactivation terms, please refer to our complete terms documentation available online.`;

// Default templates for Yield Product deactivation - FULL VERSION (WEB)
const DEFAULT_TERMS_TOGGLE_OFF_FULL = `FIDDUX YIELD PRODUCT DEACTIVATION TERMS

⚠️ IMPORTANT NOTICE
Deactivating your participation may invalidate your eligibility for yield accumulation until the end of the current month. If you deactivate during an active month, you may not be eligible to reactivate and earn yield for the remainder of that month. Please consider this carefully before proceeding.

Governing Law and Jurisdiction
This Yield Product Deactivation Confirmation ("Confirmation") is governed by the laws of the Republic of Estonia. Any disputes shall be subject to the exclusive jurisdiction of the courts of Tallinn, Estonia.

Deactivation Terms
By deactivating this Yield Product, you acknowledge and agree that:

Deactivation Effect:
Your account balance will stop participating in this Yield Product, managed with our third-party partners, starting the day after deactivation.
Any yield accumulated up to the deactivation date will be preserved and included in your final payment when the product period ends, subject to verification.

Monthly Eligibility Impact:
If you deactivate during an active month, you may not be eligible to reactivate and earn yield for the remainder of that calendar month. Eligibility for yield accumulation will resume in the following month if you choose to reactivate.

Accumulated Yield:
Yield earned during your participation period will remain in your account and be processed at the end of the product period.
Final yield payment amounts are subject to administrator and partner verification. The administrator's determination is final and binding.

Future Participation:
Future Yield Products may involve different third-party partners and will require acceptance of then-current terms.
If you reactivate in a future month, you will need to accept the then-current terms and conditions.

Limitation of Liability:
FIDDUX and its third-party partners shall not be liable for any losses arising from deactivation or yield processing, except as required by Estonian law.

Acknowledgment:
You understand that deactivating will stop future yield accumulation from the following day.
You acknowledge that deactivating during an active month may invalidate your eligibility for yield accumulation for the remainder of that month.
You confirm that administrator decisions regarding yield payments are final and binding.

By checking the box below, you confirm your acceptance of this deactivation confirmation.`;

const YieldProductManagement = () => {
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [finalSubmissions, setFinalSubmissions] = useState(new Map()); // Map<product_id, boolean>
  const [participantCounts, setParticipantCounts] = useState(new Map()); // Map<product_id, number>
  const [participantsDialogOpen, setParticipantsDialogOpen] = useState(false);
  const [selectedProductParticipants, setSelectedProductParticipants] = useState(null);
  const [participantsList, setParticipantsList] = useState([]);
  const [loadingParticipants, setLoadingParticipants] = useState(false);
  const [sendingPdf, setSendingPdf] = useState(new Set()); // Track which participants are sending PDF
  const [participantsSortBy, setParticipantsSortBy] = useState('name'); // 'name', 'balance', 'toggle_on', 'toggle_off'
  const [searchProduct, setSearchProduct] = useState("");
  const debouncedSearch = useDebounce(searchProduct, 500);
  const [usersMap, setUsersMap] = useState({});
  const [clientSegment, setClientSegment] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [periodStartDate, setPeriodStartDate] = useState(getDefaultFundsPeriodStart);
  const [periodEndDate, setPeriodEndDate] = useState(getDefaultFundsPeriodEnd);
  const [dateFilterActive, setDateFilterActive] = useState(false);
  const [datePickerResetKey, setDatePickerResetKey] = useState(0);

  const { totals: periodTotals, loading: periodTotalsLoading } = useYieldProductsPeriodTotals({
    startDate: periodStartDate,
    endDate: periodEndDate,
  });

  const filtersActive = hasActiveFundsFilters({
    search: searchProduct,
    status: statusFilter,
    clientSegment,
    dateFilterActive,
    periodStartDate,
    periodEndDate,
  });

  const resetAllFilters = () => {
    setSearchProduct("");
    setStatusFilter(null);
    setClientSegment(null);
    setDateFilterActive(false);
    setPeriodStartDate(getDefaultFundsPeriodStart());
    setPeriodEndDate(getDefaultFundsPeriodEnd());
    setDatePickerResetKey((key) => key + 1);
  };

  const handleClientSegmentChange = (segment) => {
    setClientSegment(segment);
  };

  const handlePeriodStartChange = (value) => {
    if (!value) return;
    setDateFilterActive(true);
    setPeriodStartDate(value);
    if (periodEndDate && value.isAfter(periodEndDate)) {
      setPeriodEndDate(value);
    }
  };

  const handlePeriodEndChange = (value) => {
    if (!value) return;
    setDateFilterActive(true);
    setPeriodEndDate(value);
    if (periodStartDate && value.isBefore(periodStartDate)) {
      setPeriodStartDate(value);
    }
  };

  const filteredProducts = useMemo(() => {
    let rows = products;

    if (debouncedSearch?.trim()) {
      const term = debouncedSearch.trim().toLowerCase();
      rows = rows.filter((product) =>
        product.product_name?.toLowerCase().includes(term)
      );
    }

    if (dateFilterActive && periodStartDate && periodEndDate) {
      rows = filterYieldProductsByPeriod(rows, periodStartDate, periodEndDate);
    }

    if (clientSegment) {
      rows = rows.filter((product) =>
        productMatchesClientSegment(product, clientSegment, usersMap)
      );
    }

    if (statusFilter) {
      rows = rows.filter((product) =>
        matchesYieldProductStatus(product, statusFilter, finalSubmissions)
      );
    }

    return rows;
  }, [
    products,
    debouncedSearch,
    dateFilterActive,
    periodStartDate,
    periodEndDate,
    clientSegment,
    statusFilter,
    usersMap,
    finalSubmissions,
  ]);

  // Fetch products from Firestore
  useEffect(() => {
    setIsLoading(true);
    
    // Use onSnapshot without orderBy to avoid index requirement
    const unsubscribe = onSnapshot(
      collection(DATABASE, "yields_products"),
      (snapshot) => {
        const data = snapshot.docs
          .map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }))
          .sort((a, b) => {
            const aTime = a.created_at?.toMillis() || 0;
            const bTime = b.created_at?.toMillis() || 0;
            return bTime - aTime;
          });
        setProducts(data);
        setIsLoading(false);
      },
      (error) => {
        console.error("Error fetching yield products:", error);
        toast.error("Failed to load yield products");
        setIsLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  // Fetch all users for access control
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const usersQuery = query(
          collection(DATABASE, "users"),
          where("role", "==", 1),
          where("status", "==", 1)
        );
        const usersSnapshot = await getDocs(usersQuery);
        const users = usersSnapshot.docs.map((doc) => ({
          id: doc.id,
          name: doc.data().full_name || doc.data().name || doc.data().email,
          email: doc.data().email,
        }));
        setAllUsers(users);
      } catch (error) {
        console.error("Error fetching users:", error);
        toast.error("Failed to load users for access control");
      }
    };
    fetchUsers();
  }, []);

  // Fetch final submissions to check if products are finalized
  useEffect(() => {
    const fetchFinalSubmissions = async () => {
      try {
        const finalSnapshot = await getDocs(collection(DATABASE, "yields_final"));
        const finalMap = new Map();
        finalSnapshot.docs.forEach((doc) => {
          const data = doc.data();
          if (data.product_id) {
            finalMap.set(data.product_id, true);
          }
        });
        setFinalSubmissions(finalMap);
      } catch (error) {
        console.error("Error fetching final submissions:", error);
      }
    };
    fetchFinalSubmissions();
  }, []);

  // Fetch participant counts and users map for segment filtering
  useEffect(() => {
    const activeUsersQuery = query(
      collection(DATABASE, "users"),
      where("yield_active", "==", true)
    );

    const unsubscribe = onSnapshot(
      activeUsersQuery,
      (activeUsersSnapshot) => {
        const countsMap = new Map();
        const map = {};

        activeUsersSnapshot.docs.forEach((userDoc) => {
          const userData = userDoc.data();
          map[userDoc.id] = { doc_id: userDoc.id, ...userData };

          const productId = userData.yield_active_product_id;
          if (productId) {
            countsMap.set(productId, (countsMap.get(productId) || 0) + 1);
          }
        });

        setUsersMap(map);
        setParticipantCounts(countsMap);
      },
      (error) => {
        console.error("Error listening to active yield users:", error);
      }
    );

    return () => unsubscribe();
  }, []);

  // Formik validation schema
  const validationSchema = Yup.object({
    product_name: Yup.string().required("Product name is required"),
    approx_percentage: Yup.string().required("Approximate percentage is required"),
    period_months: Yup.number()
      .required("Period is required")
      .min(1, "Period must be at least 1 month")
      .max(24, "Period cannot exceed 24 months"),
    start_date: Yup.date().required("Start date is required"),
    end_date: Yup.date()
      .required("End date is required")
      .min(Yup.ref("start_date"), "End date must be after start date"),
    default_commission_percentage: Yup.number()
      .required("Default commission percentage is required")
      .min(0, "Commission cannot be negative")
      .max(100, "Commission cannot exceed 100"),
    access_type: Yup.string().required("Access type is required").oneOf(["public", "restricted"]),
    allowed_user_ids: Yup.array().of(Yup.string()),
    terms_toggle_on_simple: Yup.string().required("Simple terms for toggle ON are required (APP)"),
    terms_toggle_on_full: Yup.string().required("Full terms for toggle ON are required (WEB)"),
    terms_toggle_off_simple: Yup.string().required("Simple terms for toggle OFF are required (APP)"),
    terms_toggle_off_full: Yup.string().required("Full terms for toggle OFF are required (WEB)"),
  });

  // Formik setup
  const formik = useFormik({
    initialValues: {
      product_name: "",
      approx_percentage: "",
      period_months: "",
      start_date: "",
      end_date: "",
      active: true,
      default_commission_percentage: 30,
      access_type: "public",
      allowed_user_ids: [],
      terms_toggle_on_simple: "",
      terms_toggle_on_full: "",
      terms_toggle_off_simple: "",
      terms_toggle_off_full: "",
    },
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      try {
        const productData = {
          product_name: values.product_name,
          approx_percentage: values.approx_percentage,
          period_months: parseInt(values.period_months),
          start_date: Timestamp.fromDate(new Date(values.start_date)),
          end_date: Timestamp.fromDate(new Date(values.end_date)),
          active: values.active,
          default_commission_percentage: parseFloat(values.default_commission_percentage),
          access_type: values.access_type,
          allowed_user_ids: values.access_type === "restricted" ? values.allowed_user_ids : [],
          terms_toggle_on_simple: values.terms_toggle_on_simple,
          terms_toggle_on_full: values.terms_toggle_on_full,
          terms_toggle_off_simple: values.terms_toggle_off_simple,
          terms_toggle_off_full: values.terms_toggle_off_full,
          updated_at: Timestamp.now(),
        };

        if (editMode && selectedProduct) {
          // Prevent changing access_type for existing products (can cause data inconsistencies)
          const originalAccessType = selectedProduct.access_type || "public";
          if (values.access_type !== originalAccessType) {
            toast.error("Cannot change access type (Public/Restricted) for existing products. This can cause data inconsistencies and affect active participants. Please create a new product instead.");
            return;
          }
          
          // Update existing product
          await updateDoc(doc(DATABASE, "yields_products", selectedProduct.id), productData);
          toast.success("Yield product updated successfully!");
        } else {
          // Create new product
          productData.created_at = Timestamp.now();
          productData.product_id = `yield_${Date.now()}`;
          await addDoc(collection(DATABASE, "yields_products"), productData);
          toast.success("Yield product created successfully!");
        }

        resetForm();
        setOpenDialog(false);
        setEditMode(false);
        setSelectedProduct(null);
      } catch (error) {
        console.error("Error saving yield product:", error);
        toast.error("Failed to save yield product");
      }
    },
  });

  // Handle edit
  const handleEdit = (product) => {
    setSelectedProduct(product);
    setEditMode(true);
    formik.setValues({
      product_name: product.product_name || "",
      approx_percentage: product.approx_percentage || "",
      period_months: product.period_months || "",
      start_date: product.start_date
        ? dayjs(product.start_date.toDate()).format("YYYY-MM-DD")
        : "",
      end_date: product.end_date
        ? dayjs(product.end_date.toDate()).format("YYYY-MM-DD")
        : "",
      active: product.active !== undefined ? product.active : true,
      default_commission_percentage: product.default_commission_percentage !== undefined ? product.default_commission_percentage : 30,
      access_type: (product.access_type === "public" || product.access_type === "restricted") ? product.access_type : "public",
      allowed_user_ids: Array.isArray(product.allowed_user_ids) ? product.allowed_user_ids : [],
      terms_toggle_on_simple: product.terms_toggle_on_simple || product.terms_toggle_on || "",
      terms_toggle_on_full: product.terms_toggle_on_full || product.terms_toggle_on || "",
      terms_toggle_off_simple: product.terms_toggle_off_simple || product.terms_toggle_off || "",
      terms_toggle_off_full: product.terms_toggle_off_full || product.terms_toggle_off || "",
    });
    setOpenDialog(true);
  };

  // Handle delete with safety checks
  const handleDelete = async (productId) => {
    try {
      // 1. Check if there are active participants
      const activeUsersSnapshot = await getDocs(
        query(
          collection(DATABASE, "users"),
          where("yield_active", "==", true),
          where("yield_active_product_id", "==", productId)
        )
      );

      const activeUsersCount = activeUsersSnapshot.size;

      if (activeUsersCount > 0) {
        // Product has active participants - show warning
        const result = await Swal.fire({
          icon: "warning",
          title: "⚠️ Product Has Active Participants",
          html: `
            <p>This product has <strong>${activeUsersCount} active participant(s)</strong>.</p>
            <p><strong>Deleting this product will:</strong></p>
            <ul style="text-align: left; margin: 20px 0;">
              <li>Clean up orphaned references in user accounts</li>
              <li>Preserve all historical data (monthly records, final submissions, transactions)</li>
              <li>May require manual cleanup of historical records later</li>
            </ul>
            <p><strong>Recommended:</strong> Set product to <code>active: false</code> instead of deleting.</p>
          `,
          showCancelButton: true,
          confirmButtonText: "Delete Anyway (Clean References)",
          cancelButtonText: "Cancel",
          confirmButtonColor: "#d32f2f",
          cancelButtonColor: "#1976d2",
        });

        if (!result.isConfirmed) {
          return; // User cancelled
        }

        // Final confirmation
        const finalConfirm = await Swal.fire({
          icon: "error",
          title: "⚠️ Final Confirmation Required",
          html: `
            <p>Are you absolutely sure?</p>
            <p>This will affect <strong>${activeUsersCount} user(s)</strong>.</p>
            <p><small>Historical data will be preserved, but active references will be cleaned.</small></p>
          `,
          showCancelButton: true,
          confirmButtonText: "Yes, Delete",
          cancelButtonText: "Cancel",
          confirmButtonColor: "#d32f2f",
        });

        if (!finalConfirm.isConfirmed) {
          return;
        }

        // Clean up orphaned references before deleting product
        const batch = writeBatch(DATABASE);
        
        activeUsersSnapshot.docs.forEach((userDoc) => {
          // Only clean up active references - preserve all historical data
          batch.update(userDoc.ref, {
            yield_active: false,
            yield_active_product_id: null,
            yield_deactivation_source: "admin_product_delete",
            // NOTE: yield_days_participated, yield_total_earned, yield_toggle_history are preserved
          });
        });

        await batch.commit();
        
        // Now delete the product
        await deleteDoc(doc(DATABASE, "yields_products", productId));
        
        toast.success(
          `Product deleted. Cleaned up ${activeUsersCount} orphaned reference(s). Historical data preserved.`
        );
      } else {
        // No active participants - simple confirmation
        const confirmed = window.confirm(
          "Are you sure you want to delete this yield product?"
        );
        if (!confirmed) return;

        await deleteDoc(doc(DATABASE, "yields_products", productId));
        toast.success("Yield product deleted successfully!");
      }
    } catch (error) {
      console.error("Error deleting yield product:", error);
      toast.error("Failed to delete yield product");
    }
  };

  // Handle add new
  const handleAddNew = () => {
    setEditMode(false);
    setSelectedProduct(null);
    // Set default templates when creating new product (both simple and full versions)
    formik.setValues({
      product_name: "",
      approx_percentage: "",
      period_months: "",
      start_date: "",
      end_date: "",
      active: true,
      default_commission_percentage: 30,
      access_type: "public",
      allowed_user_ids: [],
      terms_toggle_on_simple: DEFAULT_TERMS_TOGGLE_ON_SIMPLE,
      terms_toggle_on_full: DEFAULT_TERMS_TOGGLE_ON_FULL,
      terms_toggle_off_simple: DEFAULT_TERMS_TOGGLE_OFF_SIMPLE,
      terms_toggle_off_full: DEFAULT_TERMS_TOGGLE_OFF_FULL,
    });
    setOpenDialog(true);
  };

  // Handle dialog close
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditMode(false);
    setSelectedProduct(null);
    formik.resetForm();
  };

  // Sort participants based on selected criteria
  const sortParticipants = (participants, sortBy) => {
    const sorted = [...participants];
    
    switch (sortBy) {
      case 'name':
        return sorted.sort((a, b) => a.userName.localeCompare(b.userName));
      case 'balance':
        return sorted.sort((a, b) => b.currentBalance - a.currentBalance); // Descending
      case 'toggle_on':
        return sorted.sort((a, b) => {
          if (!a.toggledOnAt) return 1;
          if (!b.toggledOnAt) return -1;
          return b.toggledOnAt.getTime() - a.toggledOnAt.getTime(); // Most recent first
        });
      case 'toggle_off':
        return sorted.sort((a, b) => {
          if (!a.toggledOffAt) return 1;
          if (!b.toggledOffAt) return -1;
          return b.toggledOffAt.getTime() - a.toggledOffAt.getTime(); // Most recent first
        });
      default:
        return sorted;
    }
  };

  // Handle sort change
  const handleSortChange = (event) => {
    const newSortBy = event.target.value;
    setParticipantsSortBy(newSortBy);
    const sortedList = sortParticipants(participantsList, newSortBy);
    setParticipantsList(sortedList);
  };

  // Handle view participants
  const handleViewParticipants = async (product) => {
    setSelectedProductParticipants(product);
    setLoadingParticipants(true);
    setParticipantsDialogOpen(true);
    
    try {
      // CRITICAL: We need to check BOTH document ID and product_id field
      // Some users have document ID (after auto-migration), others still have product_id field
      const documentId = product.id; // Ex: govyOQ31oMbuCIt7f2Nz
      const productIdField = product.product_id; // Ex: yield_1768842686103
      
      // Fetch ALL users with yield_active = true (cannot use OR in Firestore where clause)
      const activeUsersQuery = query(
        collection(DATABASE, "users"),
        where("yield_active", "==", true)
      );
      const activeUsersSnapshot = await getDocs(activeUsersQuery);
      
      // Filter users that match EITHER document ID OR product_id field
      const matchingUsers = activeUsersSnapshot.docs.filter(doc => {
        const userData = doc.data();
        const userProductId = userData.yield_active_product_id;
        return userProductId === documentId || userProductId === productIdField;
      });
      
      const participants = await Promise.all(
        matchingUsers.map(async (userDoc) => {
          const userData = userDoc.data();
          const userId = userDoc.id;
          
          // Get current balance from net_balance or calculate from ledgers
          const netBalance = userData.net_balance || 0;
          
          // Get yield toggle timestamps from yield_toggle_history
          const toggleHistory = userData.yield_toggle_history || [];
          
          // Find last "on" and last "off" actions
          let lastToggleOn = null;
          let lastToggleOff = null;
          
          // Iterate through history to find most recent ON and OFF
          toggleHistory.forEach(entry => {
            if (entry.action === 'on' && entry.timestamp) {
              if (!lastToggleOn || entry.timestamp > lastToggleOn) {
                lastToggleOn = entry.timestamp;
              }
            } else if (entry.action === 'off' && entry.timestamp) {
              if (!lastToggleOff || entry.timestamp > lastToggleOff) {
                lastToggleOff = entry.timestamp;
              }
            }
          });
          
          // Convert timestamps to Date objects (timestamp is already in milliseconds)
          const toggledOnAt = lastToggleOn ? new Date(lastToggleOn) : null;
          const toggledOffAt = lastToggleOff ? new Date(lastToggleOff) : null;
          
          return {
            userId,
            userName: userData.full_name || userData.name || userData.email || 'Unknown',
            userEmail: userData.email || '',
            currentBalance: netBalance,
            toggledOnAt,
            toggledOffAt,
            productId: userData.yield_active_product_id, // The actual ID the user has
            productName: product.product_name || 'Unknown Product',
          };
        })
      );
      
      // Sort participants based on current sort setting
      const sortedParticipants = sortParticipants(participants, participantsSortBy);
      setParticipantsList(sortedParticipants);
    } catch (error) {
      console.error("Error fetching participants:", error);
      toast.error("Failed to load participants");
    } finally {
      setLoadingParticipants(false);
    }
  };

  // Handle send PDF to client
  const handleSendPdfToClient = async (participant) => {
    const { userId, userName, productId, productName } = participant;

    setSendingPdf(prev => new Set(prev).add(userId));
    
    try {
      const response = await fetch(FUNCTIONS_URLS.SendYieldTermsPdfToClient, {
        method: 'POST',
        headers: await adminAuthHeaders(),
        body: JSON.stringify({
          userId,
          userName,
          productId,
          productName,
        }),
      });
      
      const result = await response.json();
      
      if (response.ok) {
        toast.success(`PDF sent successfully to ${result.sentTo || result.wouldSendTo}`);
      } else {
        throw new Error(result.error || 'Failed to send PDF');
      }
    } catch (error) {
      console.error("Error sending PDF:", error);
      toast.error(`Failed to send PDF: ${error.message}`);
    } finally {
      setSendingPdf(prev => {
        const newSet = new Set(prev);
        newSet.delete(userId);
        return newSet;
      });
    }
  };

  // DataGrid columns
  const columns = [
    {
      field: "product_name",
      headerName: "Product Name",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "approx_percentage",
      headerName: "APY",
      width: 110,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color="primary"
          size="small"
          sx={{ fontWeight: "bold" }}
        />
      ),
    },
    {
      field: "period_months",
      headerName: "Period",
      width: 85,
      renderCell: (params) => `${params.value} months`,
    },
    {
      field: "start_date",
      headerName: "Start Date",
      width: 110,
      renderCell: (params) =>
        params.value ? dayjs(params.value.toDate()).format("DD/MM/YYYY") : "N/A",
    },
    {
      field: "end_date",
      headerName: "End Date",
      width: 110,
      renderCell: (params) =>
        params.value ? dayjs(params.value.toDate()).format("DD/MM/YYYY") : "N/A",
    },
    {
      field: "default_commission_percentage",
      headerName: "Commission",
      width: 90,
      renderCell: (params) => (
        <Chip
          label={`${params.value !== undefined ? params.value : 30}%`}
          color="secondary"
          size="small"
          sx={{ fontWeight: "bold" }}
        />
      ),
    },
    {
      field: "access_type",
      headerName: "Access",
      width: 110,
      renderCell: (params) => {
        const accessType = params.value || "public"; // Default to public if undefined
        return (
          <Chip
            label={accessType === "public" ? "Public" : "Restricted"}
            color={accessType === "public" ? "default" : "info"}
            size="small"
            sx={{
              ...(accessType === "public" && {
                backgroundColor: "#26A69A", // Teal color for Public (clearly different from Active green and Restricted blue)
                color: "#ffffff",
                fontWeight: 500,
              }),
              ...(accessType === "restricted" && {
                backgroundColor: "#1976d2", // Blue color for Restricted
                color: "#ffffff",
                fontWeight: 500,
              }),
            }}
          />
        );
      },
    },
    {
      field: "allowed_users_count",
      headerName: "Users",
      width: 70,
      valueGetter: (value, row) => {
        const accessType = row.access_type || "public"; // Default to public if undefined
        if (accessType === "public") return "All";
        return row.allowed_user_ids?.length || 0;
      },
    },
    {
      field: "participants",
      headerName: "Participants",
      width: 130,
      renderCell: (params) => {
        // CRITICAL: Count BOTH document ID and product_id field
        // Some users have document ID (migrated), others have product_id field (not yet migrated)
        const documentId = params.row.id;
        const productIdField = params.row.product_id;
        const countByDocId = participantCounts.get(documentId) || 0;
        const countByFieldId = participantCounts.get(productIdField) || 0;
        const count = countByDocId + countByFieldId; // Sum both counts
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, height: '100%' }}>
            <Typography variant="body2" sx={{ fontWeight: 500, color: count > 0 ? "primary.main" : "text.secondary" }}>
              {count}
            </Typography>
            {count > 0 && (
              <IconButton
                size="small"
                color="primary"
                onClick={(e) => {
                  e.stopPropagation();
                  handleViewParticipants(params.row);
                }}
                sx={{ padding: '4px' }}
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        );
      },
    },
    {
      field: "days_remaining",
      headerName: "Days Remaining",
      width: 130,
      renderCell: (params) => {
        const row = params.row;
        const today = dayjs();
        const endDate = row.end_date ? dayjs(row.end_date.toDate()) : null;
        const hasFinalSubmission = finalSubmissions.get(row.product_id) || false;
        
        if (hasFinalSubmission || !endDate) {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>—</Typography>
            </Box>
          );
        }
        
        const daysUntilEnd = endDate.diff(today, 'day');
        const daysPastEnd = today.diff(endDate, 'day');
        
        if (daysPastEnd > 0) {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
              <Chip
                label={`${daysPastEnd} day${daysPastEnd !== 1 ? 's' : ''} past`}
                color="error"
                size="small"
                sx={{ fontWeight: 500 }}
              />
            </Box>
          );
        } else if (daysUntilEnd <= 30) {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
              <Chip
                label={`${daysUntilEnd} day${daysUntilEnd !== 1 ? 's' : ''}`}
                color="warning"
                size="small"
                sx={{ fontWeight: 500 }}
              />
            </Box>
          );
        } else {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                {daysUntilEnd} day{daysUntilEnd !== 1 ? 's' : ''}
              </Typography>
            </Box>
          );
        }
      },
    },
    {
      field: "active",
      headerName: "Status",
      width: 120,
      renderCell: (params) => {
        const row = params.row;
        const today = dayjs();
        const endDate = row.end_date ? dayjs(row.end_date.toDate()) : null;
        const hasFinalSubmission = finalSubmissions.get(row.product_id) || false;
        
        // Determine status - Products don't "expire", they are finalized
        let statusLabel = "Inactive";
        let statusColor = "default";
        let customSx = {};
        
        if (hasFinalSubmission) {
          statusLabel = "Finalized";
          statusColor = "default";
          customSx = {
            backgroundColor: "#757575", // Grey color for finalized
            color: "#ffffff",
            fontWeight: 500,
          };
        } else if (params.value) {
          statusLabel = "Active";
          statusColor = "success"; // Green for active
        }
        
        return (
          <Chip
            label={statusLabel}
            color={statusColor}
            size="small"
            sx={Object.keys(customSx).length > 0 ? customSx : undefined}
          />
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <IconButton
            size="small"
            color="primary"
            onClick={() => handleEdit(params.row)}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            color="error"
            onClick={() => handleDelete(params.row.id)}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box
      sx={{
        width: "100%",
        paddingTop: { xs: "60px", sm: "0px" },
        p: { xs: 2, md: 3 },
        bgcolor: "background.default",
      }}
    >
      <FundsPageShell
        title="Yield Products"
        headerMeta={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddNew}
            sx={{
              background: "linear-gradient(135deg, #2af598 0%, #00ba2d 100%)",
              color: "#fff",
              height: "36px",
              px: 2,
              fontSize: 13,
              fontWeight: 600,
              textTransform: "none",
              borderRadius: "10px",
              "&:hover": {
                background: "linear-gradient(135deg, #3adecc 0%, #2af598 100%)",
              },
            }}
          >
            Add New Product
          </Button>
        }
        headerExtra={(() => {
          const today = dayjs();
          const productsNeedingAttention = products.filter((product) => {
            if (!product.active || !product.end_date) return false;
            const hasFinalSubmission = finalSubmissions.get(product.product_id) || false;
            if (hasFinalSubmission) return false;

            const endDate = dayjs(product.end_date.toDate());
            const daysUntilEnd = endDate.diff(today, "day");
            const daysPastEnd = today.diff(endDate, "day");

            return (daysUntilEnd <= 30 && daysUntilEnd >= 0) || daysPastEnd > 0;
          });

          if (productsNeedingAttention.length === 0) return null;

          return (
            <Box sx={{ mt: 2 }}>
              {productsNeedingAttention.map((product) => {
                const endDate = dayjs(product.end_date.toDate());
                const daysUntilEnd = endDate.diff(today, "day");
                const daysPastEnd = today.diff(endDate, "day");
                const isPastEnd = daysPastEnd > 0;

                return (
                  <Alert
                    key={product.id}
                    severity={isPastEnd ? "error" : "warning"}
                    sx={{ mb: 1.5 }}
                    action={
                      <Button
                        variant="contained"
                        color={isPastEnd ? "error" : "warning"}
                        size="medium"
                        sx={{
                          fontWeight: "bold",
                          minWidth: "140px",
                          fontSize: "0.95rem",
                          textTransform: "none",
                        }}
                        onClick={() => {
                          window.location.href = "/admin/yield-final";
                        }}
                      >
                        Finalize Now
                      </Button>
                    }
                  >
                    <Typography variant="body1" sx={{ fontWeight: "bold", mb: 0.5 }}>
                      {isPastEnd
                        ? `⚠️ "${product.product_name}" ended ${daysPastEnd} day${daysPastEnd !== 1 ? "s" : ""} ago and needs to be finalized`
                        : `⚠️ "${product.product_name}" ends in ${daysUntilEnd} day${daysUntilEnd !== 1 ? "s" : ""} - Prepare for final submission`}
                    </Typography>
                    <Typography variant="body2">
                      End Date: {endDate.format("DD/MM/YYYY")} ·
                      {isPastEnd
                        ? " Please complete Yield Final Submission to close this product."
                        : " Please ensure all monthly yields are processed and prepare for final submission."}
                    </Typography>
                  </Alert>
                );
              })}
            </Box>
          );
        })()}
        totalsProps={{
          totals: periodTotals,
          loading: periodTotalsLoading,
          startDate: periodStartDate,
          endDate: periodEndDate,
          type: "yieldProducts",
        }}
        toolbar={
          <FundsPageToolbar
            searchValue={searchProduct}
            onSearchChange={(event) => setSearchProduct(event.target.value)}
            onSearchClear={() => setSearchProduct("")}
            searchPlaceholder="Search product name..."
            periodStartDate={periodStartDate}
            periodEndDate={periodEndDate}
            onPeriodStartChange={handlePeriodStartChange}
            onPeriodEndChange={handlePeriodEndChange}
            resetKey={datePickerResetKey}
            filterActionsProps={{
              statusList: YIELD_PRODUCT_STATUS_OPTIONS,
              statusValue: statusFilter,
              onStatusChange: (e) =>
                setStatusFilter(e.target.value === "" ? null : e.target.value),
              statusPlaceholder: "Status",
              clientSegment,
              onClientSegmentChange: handleClientSegmentChange,
              onResetFilters: resetAllFilters,
              filtersActive,
              showStatusFilter: true,
            }}
          />
        }
      />

      <Card sx={{ mt: 3 }}>
        <CardContent>
          <DataGrid
            rows={filteredProducts}
            columns={columns}
            loading={isLoading}
            autoHeight
            pageSize={10}
            rowsPerPageOptions={[10, 25, 50]}
            disableSelectionOnClick
            sx={{
              "& .MuiDataGrid-root": {
                border: "none",
              },
              "& .MuiDataGrid-cell": {
                borderBottom: "1px solid #f0f0f0",
              },
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#f5f5f5",
                borderBottom: "2px solid #e0e0e0",
              },
            }}
          />
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          {editMode ? "Edit Yield Product" : "Add New Yield Product"}
        </DialogTitle>
        <form onSubmit={formik.handleSubmit}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Product Name"
                  name="product_name"
                  value={formik.values.product_name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.product_name &&
                    Boolean(formik.errors.product_name)
                  }
                  helperText={
                    formik.touched.product_name && formik.errors.product_name
                  }
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Approximate Percentage (e.g., 12-15%)"
                  name="approx_percentage"
                  value={formik.values.approx_percentage}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.approx_percentage &&
                    Boolean(formik.errors.approx_percentage)
                  }
                  helperText={
                    formik.touched.approx_percentage &&
                    formik.errors.approx_percentage
                  }
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Period (Months)"
                  name="period_months"
                  value={formik.values.period_months}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.period_months &&
                    Boolean(formik.errors.period_months)
                  }
                  helperText={
                    formik.touched.period_months && formik.errors.period_months
                  }
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  type="date"
                  label="Start Date"
                  name="start_date"
                  value={formik.values.start_date}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  InputLabelProps={{ shrink: true }}
                  error={
                    formik.touched.start_date && Boolean(formik.errors.start_date)
                  }
                  helperText={
                    formik.touched.start_date && formik.errors.start_date
                  }
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  type="date"
                  label="End Date"
                  name="end_date"
                  value={formik.values.end_date}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  InputLabelProps={{ shrink: true }}
                  error={
                    formik.touched.end_date && Boolean(formik.errors.end_date)
                  }
                  helperText={formik.touched.end_date && formik.errors.end_date}
                />
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography>Active:</Typography>
                  <Switch
                    name="active"
                    checked={formik.values.active}
                    onChange={formik.handleChange}
                    color="primary"
                  />
                </Box>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="number"
                  label="Default Commission Percentage (%)"
                  name="default_commission_percentage"
                  value={formik.values.default_commission_percentage}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.default_commission_percentage &&
                    Boolean(formik.errors.default_commission_percentage)
                  }
                  helperText={
                    (formik.touched.default_commission_percentage &&
                      formik.errors.default_commission_percentage) ||
                    "Default: 30% (similar to Performance commission)"
                  }
                  inputProps={{ step: "0.01", min: "0", max: "100" }}
                />
              </Grid>

              {/* Access Control Section */}
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mt: 2, mb: 1, color: "primary.main" }}>
                  Access Control
                </Typography>
              </Grid>

              {editMode && (
                <Grid item xs={12}>
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    <strong>Access Type cannot be changed:</strong> Changing access type (Public/Restricted) for existing products can cause data inconsistencies and affect active participants. Please create a new product instead.
                  </Alert>
                </Grid>
              )}

              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Access Type</InputLabel>
                  <Select
                    name="access_type"
                    value={formik.values.access_type || "public"}
                    onChange={(e) => {
                      formik.handleChange(e);
                      if (e.target.value === "public") {
                        formik.setFieldValue("allowed_user_ids", []);
                      }
                    }}
                    label="Access Type"
                    disabled={editMode}
                  >
                    <MenuItem value="public">Public (All Users)</MenuItem>
                    <MenuItem value="restricted">Restricted (Selected Users Only)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {formik.values.access_type === "restricted" && (
                <Grid item xs={12}>
                  <Autocomplete
                    multiple
                    options={allUsers}
                    getOptionLabel={(option) => `${option.name} (${option.email})`}
                    value={allUsers.filter((user) =>
                      formik.values.allowed_user_ids.includes(user.id)
                    )}
                    onChange={(event, newValue) => {
                      formik.setFieldValue(
                        "allowed_user_ids",
                        newValue.map((user) => user.id)
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Allowed Users"
                        placeholder="Select users who can access this product"
                        helperText={`${formik.values.allowed_user_ids.length} user(s) selected`}
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => {
                        const { key, ...tagProps } = getTagProps({ index });
                        return (
                          <Chip
                            key={key}
                            label={option.name}
                            {...tagProps}
                            size="small"
                          />
                        );
                      })
                    }
                  />
                </Grid>
              )}

              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mt: 2, mb: 1, color: "primary.main" }}>
                  Terms for Toggle ON
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={8}
                  label="Simple Version (APP) - ~150 words"
                  name="terms_toggle_on_simple"
                  value={formik.values.terms_toggle_on_simple}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.terms_toggle_on_simple &&
                    Boolean(formik.errors.terms_toggle_on_simple)
                  }
                  helperText={
                    formik.touched.terms_toggle_on_simple &&
                    formik.errors.terms_toggle_on_simple
                  }
                  placeholder="Simple version shown in app modal (with link to full terms)"
                  sx={{
                    "& .MuiInputBase-input": {
                      fontFamily: "monospace",
                      fontSize: "0.875rem",
                      lineHeight: 1.5,
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={15}
                  label="Full Version (WEB) - Complete Terms"
                  name="terms_toggle_on_full"
                  value={formik.values.terms_toggle_on_full}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.terms_toggle_on_full &&
                    Boolean(formik.errors.terms_toggle_on_full)
                  }
                  helperText={
                    formik.touched.terms_toggle_on_full &&
                    formik.errors.terms_toggle_on_full
                  }
                  placeholder="Full version for fiddux.com/yield-terms page"
                  sx={{
                    "& .MuiInputBase-input": {
                      fontFamily: "monospace",
                      fontSize: "0.875rem",
                      lineHeight: 1.5,
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mt: 2, mb: 1, color: "primary.main" }}>
                  Terms for Toggle OFF
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={8}
                  label="Simple Version (APP) - ~100 words"
                  name="terms_toggle_off_simple"
                  value={formik.values.terms_toggle_off_simple}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.terms_toggle_off_simple &&
                    Boolean(formik.errors.terms_toggle_off_simple)
                  }
                  helperText={
                    formik.touched.terms_toggle_off_simple &&
                    formik.errors.terms_toggle_off_simple
                  }
                  placeholder="Simple version shown in app modal (with link to full terms)"
                  sx={{
                    "& .MuiInputBase-input": {
                      fontFamily: "monospace",
                      fontSize: "0.875rem",
                      lineHeight: 1.5,
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={18}
                  label="Full Version (WEB) - Complete Terms"
                  name="terms_toggle_off_full"
                  value={formik.values.terms_toggle_off_full}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.terms_toggle_off_full &&
                    Boolean(formik.errors.terms_toggle_off_full)
                  }
                  helperText={
                    formik.touched.terms_toggle_off_full &&
                    formik.errors.terms_toggle_off_full
                  }
                  placeholder="Full version for fiddux.com/yield-terms page"
                  sx={{
                    "& .MuiInputBase-input": {
                      fontFamily: "monospace",
                      fontSize: "0.875rem",
                      lineHeight: 1.5,
                    },
                  }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} color="inherit">
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              sx={{
                background: "linear-gradient(135deg, #2af598 0%, #00ba2d 100%)",
                "&:hover": {
                  background: "linear-gradient(135deg, #3adecc 0%, #2af598 100%)",
                },
              }}
            >
              {editMode ? "Update" : "Create"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Participants Dialog */}
      <Dialog
        open={participantsDialogOpen}
        onClose={() => {
          setParticipantsDialogOpen(false);
          setSelectedProductParticipants(null);
          setParticipantsList([]);
        }}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              Participants - {selectedProductParticipants?.product_name || 'Unknown Product'}
            </Typography>
            {!loadingParticipants && participantsList.length > 0 && (
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={participantsSortBy}
                  onChange={handleSortChange}
                  label="Sort By"
                >
                  <MenuItem value="name">Name (A-Z)</MenuItem>
                  <MenuItem value="balance">Balance (High to Low)</MenuItem>
                  <MenuItem value="toggle_on">Toggle ON (Recent First)</MenuItem>
                  <MenuItem value="toggle_off">Toggle OFF (Recent First)</MenuItem>
                </Select>
              </FormControl>
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          {loadingParticipants ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : participantsList.length === 0 ? (
            <Typography variant="body2" sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
              No active participants found for this product.
            </Typography>
          ) : (
            <TableContainer component={Paper} sx={{ mt: 2, maxHeight: 650 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontSize: '0.95rem', fontWeight: 600 }}>Client Name</TableCell>
                    <TableCell sx={{ fontSize: '0.95rem', fontWeight: 600 }}>Email</TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.95rem', fontWeight: 600 }}>Current Balance</TableCell>
                    <TableCell align="center" sx={{ fontSize: '0.95rem', fontWeight: 600 }}>Toggle ON</TableCell>
                    <TableCell align="center" sx={{ fontSize: '0.95rem', fontWeight: 600 }}>Toggle OFF</TableCell>
                    <TableCell align="center" sx={{ fontSize: '0.95rem', fontWeight: 600 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {participantsList.map((participant) => (
                    <TableRow key={participant.userId} sx={{ '&:hover': { backgroundColor: 'action.hover' } }}>
                      <TableCell sx={{ fontSize: '0.9rem' }}>{participant.userName}</TableCell>
                      <TableCell sx={{ fontSize: '0.9rem' }}>{participant.userEmail}</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.9rem', fontWeight: 500 }}>
                        {dolorFormatAmount(participant.currentBalance)}
                      </TableCell>
                      <TableCell align="center">
                        {participant.toggledOnAt ? (
                          <Tooltip title={dayjs(participant.toggledOnAt).format("DD/MM/YYYY HH:mm:ss")}>
                            <Chip 
                              label={dayjs(participant.toggledOnAt).format("DD/MM/YYYY HH:mm")}
                              size="medium"
                              color="success"
                              sx={{ fontSize: '0.85rem', minWidth: 140 }}
                            />
                          </Tooltip>
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>N/A</Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {participant.toggledOffAt ? (
                          <Tooltip title={dayjs(participant.toggledOffAt).format("DD/MM/YYYY HH:mm:ss")}>
                            <Chip 
                              label={dayjs(participant.toggledOffAt).format("DD/MM/YYYY HH:mm")}
                              size="medium"
                              color="default"
                              sx={{ fontSize: '0.85rem', minWidth: 140 }}
                            />
                          </Tooltip>
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>N/A</Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Send Terms PDF to Client">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleSendPdfToClient(participant)}
                            disabled={sendingPdf.has(participant.userId)}
                          >
                            {sendingPdf.has(participant.userId) ? (
                              <CircularProgress size={20} />
                            ) : (
                              <SendIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setParticipantsDialogOpen(false);
            setSelectedProductParticipants(null);
            setParticipantsList([]);
          }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default YieldProductManagement;

